import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group-alt";
import { cleanChoice } from "@/lib/quizzes";
import { cn, renderHtmlChoiceId, renderMarkdown } from "@/lib/utils";
import { useQuestion } from "@/store/question.context";
import { Choice, ChoiceType, CompositeChoice } from "@/types/questionnaries";
import { FC, useEffect, useRef, useState } from "react";
import ChoiceList from "./choice-list";
import FillablePart from "./fillable-part";

type SingleChoiceListProps = {
  choice: CompositeChoice;
  level?: number;
  className?: string;
  onValueChange?: (value: string | undefined) => void;
  renderChildren?: boolean;
  disabled?: boolean;
};

const loadSelectedChoice = (choices: Choice[]) => {
  return choices.find((c) => c.selected);
};

const SingleChoiceList: FC<SingleChoiceListProps> = ({
  choice,
  level = 0,
  className,
  onValueChange,
  disabled,
  renderChildren = true,
}) => {
  const getChoice = (id: string | undefined) =>
    choice.type === ChoiceType.Composite
      ? choice.choices.find((c) => c.id === id)
      : undefined;

  const [selectedChoiceId, setSelectedChoiceId] = useState<string | undefined>(
    loadSelectedChoice(choice.choices)?.id,
  );

  const selectedChoice = getChoice(selectedChoiceId);

  const [answerValue, setAnswerValue] = useState<string>(
    selectedChoice?.type === ChoiceType.Fillable
      ? selectedChoice.answerValue
      : "",
  );

  const {
    updateChoice,
    setChoices,
    isSuggestionApplied,
    setIsSuggestionApplied,
  } = useQuestion();

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  /**
   * Fires when the user selects a new choice.
   *
   */
  const handleValueChange = (selectedChoiceId: string) => {
    // Modify each item in the choice tree because each one holds its selected state
    const newChoices = choice.choices.map((choice) => {
      // Clone the choice and set its selected state
      const newChoice: Choice = {
        ...choice,
        selected: choice.id === selectedChoiceId,
      };

      // If the item is a composite and it is unselected, clean its children too
      if (newChoice.type === ChoiceType.Composite) {
        if (!newChoice.selected) {
          newChoice.choices = newChoice.choices.map(cleanChoice);
        }
      }

      // If the item is a fillable, set the answerValue to the selected one
      if (newChoice.type === ChoiceType.Fillable) {
        newChoice.answerValue =
          choice.id === selectedChoiceId ? answerValue : "";
      }

      return newChoice;
    });

    // Check if it's the root choice or a child one, and override
    // the tree or just the selected choice
    if (choice.id.includes("C")) {
      updateChoice({
        ...choice,
        choices: newChoices,
      });
    } else {
      setChoices(newChoices);
    }

    onValueChange?.(selectedChoiceId);

    setSelectedChoiceId(selectedChoiceId);
    setIsSuggestionApplied(false);
  };

  const handleTextChange = (value: string) => {
    if (selectedChoice?.type === ChoiceType.Fillable) {
      updateChoice({
        ...selectedChoice,
        answerValue: value,
      });
      setIsSuggestionApplied(false);
      setAnswerValue(value);
    }
  };

  useEffect(() => {
    onValueChange?.(selectedChoiceId);
  }, [selectedChoiceId]);

  // Set the selected choice id when the choices list changes
  useEffect(() => {
    const selectedChoice = loadSelectedChoice(choice.choices);
    setSelectedChoiceId(selectedChoice?.id);
    if (selectedChoice?.type === ChoiceType.Fillable) {
      setAnswerValue(selectedChoice?.answerValue);
    }
  }, [choice.choices]);

  return (
    <RadioGroup
      // Use "none" as the value for the UI when the ID is undefined
      // to prevent the radio group from selecting an undesired option.
      value={selectedChoiceId === undefined ? "none" : selectedChoiceId}
      onValueChange={handleValueChange}
      disabled={disabled}
      asChild
    >
      <ul className={cn("my-2 grid gap-3", className)}>
        {choice.choices.map((choice) => (
          <li
            key={choice.id}
            className="grid gap-3"
            style={{ paddingLeft: `${40 * level}px` }}
          >
            <div className="flex">
              <RadioGroupItem
                value={choice.id}
                id={renderHtmlChoiceId(choice.id)}
                className={cn(isSuggestionApplied && "text-[#F0A000]")}
              />

              <div className="grid grow">
                <Label
                  className="pl-2 pt-[5px] font-normal"
                  htmlFor={renderHtmlChoiceId(choice.id)}
                  dangerouslySetInnerHTML={{
                    __html: renderMarkdown(choice.label),
                  }}
                />

                {choice.id === selectedChoice?.id &&
                  choice?.type === ChoiceType.Fillable && (
                    <FillablePart
                      choice={choice}
                      inputRef={inputRef}
                      answerValue={answerValue}
                      handleTextChange={handleTextChange}
                      disabled={disabled}
                    />
                  )}

                {choice.id === selectedChoice?.id &&
                  choice.type === ChoiceType.Selectable &&
                  choice.statement && (
                    <p
                      className="pl-2 text-sm font-medium text-muted-foreground"
                      dangerouslySetInnerHTML={{
                        __html: renderMarkdown(choice.statement),
                      }}
                    />
                  )}
              </div>
            </div>

            {renderChildren && choice.id === selectedChoice?.id && (
              <ChoiceList
                choice={choice}
                level={level + 1}
                disabled={disabled}
              />
            )}
          </li>
        ))}
      </ul>
    </RadioGroup>
  );
};

export default SingleChoiceList;
